import React from 'react'
import useTranslation from 'next-translate/useTranslation'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { ProductDetail } from 'models/Product'
import { StyledCollapsibleMenu } from './AccurateData.styles'
import { capitalize } from 'utils/capitalize'
import Trans from 'next-translate/Trans'

type AccurateDataProps = {
  productDetail: ProductDetail
}

export const AccurateData = ({ productDetail }: AccurateDataProps) => {
  const { t } = useTranslation()

  const ONE_KG_IN_OZ = 35.274

  return (
    <Stack direction="column" gap={{ '@initial': '2', '@bp2': '3' }} css={{ width: '100%' }}>
      <StyledCollapsibleMenu label={t('e-commerce:detail.ingredients')} hasMaxWidth={false}>
        <Stack direction="column" gap="2">
          {Object.entries(productDetail.ingredients).map(([protein, ingredients]) => {
            return (
              <Stack gap="1" direction="column" key={protein}>
                {Object.keys(productDetail.analysisPercentages).length > 1 && (
                  <Text display="block" weight="medium">
                    {capitalize(protein)}
                  </Text>
                )}
                <Trans
                  i18nKey={`e-commerce:detail.ingredients-detail.${protein}`}
                  components={{ div: <div />, bold: <strong /> }}
                />
              </Stack>
            )
          })}
        </Stack>
      </StyledCollapsibleMenu>

      <StyledCollapsibleMenu label={t('e-commerce:detail.analysis')} hasMaxWidth={false}>
        <Stack direction="column" gap="2">
          {Object.entries(productDetail.analysisPercentages).map(([protein, analysis]) => {
            return (
              <Stack direction="column" gap="1" key={protein}>
                {Object.keys(productDetail.analysisPercentages).length > 1 && (
                  <Text weight="medium">{capitalize(protein)}</Text>
                )}
                <div>
                  <Stack justifyContent="space-between">
                    <Text>{t('e-commerce:detail.protein')}</Text>
                    <Text>{t(`e-commerce:${protein}.protein`)}</Text>
                  </Stack>
                  <Stack justifyContent="space-between">
                    <Text>{t('e-commerce:detail.fat')}</Text>
                    <Text>{t(`e-commerce:${protein}.fat`)}</Text>
                  </Stack>
                  <Stack justifyContent="space-between">
                    <Text>{t('e-commerce:detail.fiber')}</Text>
                    <Text>{t(`e-commerce:${protein}.fiber`)}</Text>
                  </Stack>
                  <Stack justifyContent="space-between">
                    <Text>{t('e-commerce:detail.moisture')}</Text>
                    <Text>{t(`e-commerce:${protein}.moisture`)}</Text>
                  </Stack>
                </div>
              </Stack>
            )
          })}
        </Stack>
      </StyledCollapsibleMenu>

      <StyledCollapsibleMenu label={t('e-commerce:detail.kcal')} hasMaxWidth={false}>
        <Stack direction="column" gap="2">
          {Object.entries(productDetail.kilocaloriesPerOunces).map(
            ([protein, kilocaloriesPerOunces]) => (
              <Stack key={protein} direction="column">
                <Text weight="medium">{capitalize(protein)}</Text>

                <Stack direction="column" gap="0">
                  <Text>{`${t('e-commerce:detail.kcal-per-oz', {
                    kcalPerOz: t(`e-commerce:${protein}.kcal-oz`),
                  })}`}</Text>
                  <Text>{`${t('e-commerce:detail.kcal-per-kg', {
                    kcalPerKg: t(`e-commerce:${protein}.kcal-kg`),
                  })}`}</Text>
                </Stack>
              </Stack>
            )
          )}
        </Stack>
      </StyledCollapsibleMenu>

      <StyledCollapsibleMenu label={t('e-commerce:detail.guideline')} hasMaxWidth={false}>
        {productDetail.guidelines.map((guideline, index) => (
          <p key={index}>{guideline.enUS}</p>
        ))}
      </StyledCollapsibleMenu>
    </Stack>
  )
}

AccurateData.displayName = 'AccurateData'
